import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {
  Box,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '../../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../../utils/chakraRouterLink'

import {useFaceOffRuns} from '../../../../hooks/useFaceOffRuns'
import {FaceOffRunListEntry} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'
import {FACE_OFF_GOLDEN_PATH, faceOffRunPath, fillCopy} from './constants'

export const formatStartedAt = (iso: string): string =>
  new Date(iso).toLocaleString()

const count = (n: number | null): string =>
  n === null ? copy.noCount : String(n)

/** The pass rate as Face Off shows it: 100.0% (139/139). */
export const formatPass = (run: FaceOffRunListEntry): string =>
  run.passRate === null || run.passed === null || run.total === null
    ? copy.noCount
    : fillCopy(copy.passCell, {
        rate: `${(run.passRate * 100).toFixed(1)}%`,
        passed: run.passed,
        total: run.total,
      })

const GoldenLink: React.FC = () => (
  <Box mb={4}>
    <Link
      as={ReactRouterLink}
      to={FACE_OFF_GOLDEN_PATH}
      color='var(--fo-link)'
      textDecoration='underline'
      data-testid='face-off-golden-link'>
      {copy.goldenLink}
    </Link>
  </Box>
)

const Intro: React.FC = () => (
  <Text
    fontSize='sm'
    color='var(--fo-muted)'
    mb={3}
    data-testid='face-off-intro'>
    {copy.intro}
  </Text>
)

/**
 * Lists the Face Off harness runs, newest first. Each row opens its diff. A
 * re-baseline row says so in place of its counts, which are not drift.
 */
export const FaceOffRunList: React.FC = () => {
  const {status, runs} = useFaceOffRuns()

  if (status === 'pending' || status === 'idle') {
    return (
      <Flex alignItems='center' gap={3} data-testid='face-off-loading'>
        <Spinner size='sm' />
        <Text fontSize='sm'>{copy.loading}</Text>
      </Flex>
    )
  }

  if (status === 'rejected') {
    return (
      <Text color='var(--fo-red)' fontSize='sm' data-testid='face-off-error'>
        {copy.error}
      </Text>
    )
  }

  if (runs.length === 0) {
    return (
      <>
        <Intro />
        <GoldenLink />
        <Text
          fontSize='sm'
          color='var(--fo-muted)'
          data-testid='face-off-empty'>
          {copy.empty}
        </Text>
      </>
    )
  }

  return (
    <>
      <Intro />
      <GoldenLink />
      <Table size='sm' variant='simple' data-testid='face-off-runs'>
        <Thead>
          <Tr>
            <Th>{copy.columns.started}</Th>
            <Th isNumeric>{copy.columns.goldenVersion}</Th>
            <Th isNumeric>{copy.columns.pass}</Th>
            <Th isNumeric>{copy.columns.flips}</Th>
            <Th isNumeric>{copy.columns.worse}</Th>
            <Th isNumeric>{copy.columns.better}</Th>
            <Th isNumeric>{copy.columns.shifts}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {runs.map((run) => (
            <Tr key={run.runId} data-testid='face-off-run'>
              <Td>
                <Link
                  as={ReactRouterLink}
                  to={faceOffRunPath(run.runId)}
                  color='var(--fo-link)'
                  textDecoration='underline'
                  data-testid='face-off-run-link'>
                  {formatStartedAt(run.startedAt)}
                </Link>
              </Td>
              <Td isNumeric>{run.goldenVersion}</Td>
              <Td isNumeric whiteSpace='nowrap'>
                {formatPass(run)}
              </Td>
              {run.rebaseline ? (
                <Td
                  colSpan={4}
                  color='var(--fo-muted)'
                  data-testid='face-off-run-rebaseline'>
                  {fillCopy(copy.rebaselineRow, {
                    baselineFrom: run.baselineFrom ?? '',
                  })}
                </Td>
              ) : (
                <>
                  <Td isNumeric>{run.flipCount}</Td>
                  <Td isNumeric>{count(run.regressions)}</Td>
                  <Td isNumeric>{count(run.improvements)}</Td>
                  <Td isNumeric>{count(run.shiftCount)}</Td>
                </>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  )
}
